import { MAX_ATTEMPTS, BASE_URL } from '@/constants';
import { isRetryableError, isTokenExpired } from '@/utils';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { refreshAction } from '../store/slices/auth/actions/refresh.action';
import { signOutAction } from '../store/slices/auth/actions/sign-out.action';

const baseQuery = fetchBaseQuery({
	baseUrl: BASE_URL,
	credentials: 'include',
	prepareHeaders: (headers: any, api: any) => {
		const { access } = api.getState().session;

		if (access) {
			headers.set('Authorization', `Bearer ${access.token}`);
		}
	},
});

const baseQuerySkipAuth = fetchBaseQuery({
	baseUrl: BASE_URL,
	credentials: 'include',
});

export const createError = (message: any) => ({
	status: 'CUSTOM_ERROR',
	error: message,
});

export const baseQueryWithReAuth = async (args: any, api: any, extraOptions: any) => {
	let attempts = 0;
	let response: any;

	const runBaseQuery = async () => {
		if (extraOptions?.skipAuth) {
			return baseQuerySkipAuth(args, api, extraOptions);
		}

		const { getState, dispatch } = api;
		const { access } = getState().session;

		if (!access) {
			return { error: createError('Request requires credentials') };
		}

		const tryWithRefresh = async () => {
			try {
				await dispatch(refreshAction()).unwrap();
				return baseQuery(args, api, extraOptions);
			} catch (error) {
				console.error(error);
				dispatch(signOutAction());
				return { error: createError('Failed to update session!') };
			}
		};

		if (isTokenExpired(access.payload)) {
			return tryWithRefresh();
		}

		const res = await baseQuery(args, api, extraOptions);

		if (res.error && res.error.status === 401) {
			return tryWithRefresh();
		}

		return res;
	};

	while (attempts < MAX_ATTEMPTS) {
		response = await runBaseQuery();

		if (!isRetryableError(response.error)) break;

		attempts += 1;
		console.warn(
			`Retry attempt #${attempts} due to error`,
			response.error,
			response.meta.request.method,
			response.meta.request.url
		);
	}

	if (response.error && response.error.error !== 'Request requires credentials') {
		console.error(response.error);
	}

	return response;
};
